<template>
  <!-- Panneau de restitution posé dans la `.cards-grid` des dashboards du personnel.
       Il donne un écran de lecture complet à l'agrégat `supervisorFeedbackStats/` :
       note globale et détail par plat, jusqu'ici saisis plus riches qu'ils n'étaient relus.
       La photo n'est plus restituée : l'arbitrage RGPD du 2026-08-21 (backlog B-06) a retiré
       la saisie comme l'affichage — le champ n'existe plus côté modèle. -->
  <section class="supervisor-feedback-stats" aria-label="Détail par plat">
    <div class="stats-card">
      <span class="card-header">
        <span class="card-title">📝 Détail par plat</span>
        <span v-if="!isLoading && !hasError && stats" class="badge badge-blue">{{ feedbackCountLabel }}</span>
      </span>

      <p v-if="isLoading" class="stats-state" role="status">Chargement des retours superviseur…</p>

      <p v-else-if="hasError" class="stats-state" role="alert">
        Le détail par plat n'a pas pu être chargé. Réessayez dans un instant.
      </p>

      <template v-else-if="stats">
        <!-- Bloc « note globale » : son état vide lui est propre, il ne masque pas le détail. -->
        <div class="stats-block">
          <p class="stats-block-title">Note globale</p>
          <p v-if="globalAverage !== null" class="stats-global">
            <span class="stats-value">{{ formatRating(globalAverage) }} / 5</span>
          </p>
          <p v-else class="stats-state stats-empty">Aucune note globale pour l'instant.</p>
        </div>

        <!-- Bloc « détail par plat » : son état vide lui est propre lui aussi. -->
        <div class="stats-block">
          <p class="stats-block-title">Par plat</p>
          <ul v-if="dishRows.length" class="stats-list">
            <li v-for="row in dishRows" :key="row.key" class="stats-row">
              <span class="stats-label">{{ row.label }}</span>
              <span class="stats-value stats-value--dish">{{ formatRating(row.rating) }} / 5</span>
            </li>
          </ul>
          <p v-else class="stats-state stats-empty">Aucun plat noté pour l'instant.</p>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import Constants from "@/constants"

// Catégories de `dish_ratings`, dans l'ordre du repas et avec le libellé lisible du menu.
// Miroir de la table utilisée à la saisie (SupervisorDashboard / FeedbackForm) : la note
// d'un plat est ainsi restituée en regard du plat du menu concerné.
// Les clés sont ici en camelCase car l'agrégat `supervisorFeedbackStats/` transite par
// `djangorestframework_camel_case`, qui camélise récursivement les clés du dict
// `dishRatingsAvg` : `produit_laitier` (stocké) arrive donc en `produitLaitier`.
const DISH_ORDER = ["entree", "plat", "accompagnement", "produitLaitier", "dessert", "sansViande"]
const DISH_LABELS = {
  entree: "Entrée",
  plat: "Plat principal",
  accompagnement: "Accompagnement",
  produitLaitier: "Produit laitier",
  dessert: "Dessert",
  sansViande: "Sans viande",
}

/**
 * Restitution complète de l'agrégat de retours superviseur d'une cantine sur les
 * dashboards du personnel (MANAGER / SUPERVISOR / WORKER).
 *
 * Le panneau ne choisit pas la cantine : elle lui est passée par le dashboard hôte,
 * qui la tient de `DashboardContextHeader`. La lecture passe par une action Vuex, et
 * jamais un `fetch` direct dans le composant.
 *
 * Deux blocs distincts, chacun avec son propre état vide : la note globale agrégée et
 * le détail par plat agrégé. La photo a été retirée (arbitrage RGPD du 2026-08-21,
 * backlog B-06) : ni saisie ni restituée.
 */
export default {
  name: "SupervisorFeedbackStatsPanel",

  props: {
    canteenId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      stats: null,
      status: Constants.LoadingStatus.IDLE,
    }
  },

  computed: {
    isLoading() {
      return this.status === Constants.LoadingStatus.LOADING
    },
    hasError() {
      return this.status === Constants.LoadingStatus.ERROR
    },
    globalAverage() {
      return this.stats && this.stats.avgGlobal !== undefined && this.stats.avgGlobal !== null
        ? this.stats.avgGlobal
        : null
    },
    // Les catégories notées, remises dans l'ordre du repas et étiquetées lisiblement ;
    // une clé inconnue de la table reste affichée sous sa clé brute plutôt que perdue.
    dishRows() {
      const ratings = (this.stats && this.stats.dishRatingsAvg) || {}
      const known = DISH_ORDER.filter((key) => ratings[key] !== undefined && ratings[key] !== null).map((key) => ({
        key,
        label: DISH_LABELS[key],
        rating: ratings[key],
      }))
      const extra = Object.keys(ratings)
        .filter((key) => !DISH_ORDER.includes(key) && ratings[key] !== null)
        .map((key) => ({ key, label: key, rating: ratings[key] }))
      return known.concat(extra)
    },
    feedbackCountLabel() {
      const total = (this.stats && this.stats.total) || 0
      return `${total} retour${total > 1 ? "s" : ""}`
    },
  },

  watch: {
    canteenId: "load",
  },

  mounted() {
    this.load()
  },

  methods: {
    formatRating(value) {
      if (value === null || value === undefined) return "—"
      return Number(value)
        .toFixed(1)
        .replace(".", ",")
    },
    load() {
      if (!this.canteenId) {
        this.stats = null
        this.status = Constants.LoadingStatus.IDLE
        return
      }
      this.status = Constants.LoadingStatus.LOADING
      // `Promise.resolve` protège d'un store qui ne renverrait pas de promesse
      // (cas d'un dashboard hôte dont le mock de test ne déclare pas l'action).
      return Promise.resolve(this.$store.dispatch("fetchSupervisorFeedbackStats", { canteenId: this.canteenId }))
        .then((response) => {
          this.stats = response
          this.status = Constants.LoadingStatus.SUCCESS
        })
        .catch(() => {
          this.status = Constants.LoadingStatus.ERROR
        })
    },
  },
}
</script>

<style scoped>
/* Le panneau occupe une cellule de la `.cards-grid` : sa card en prend toute la hauteur
   pour s'aligner sur ses voisines. */
.supervisor-feedback-stats {
  height: 100%;
}

/* Valeurs reprises de `.card` des dashboards hôtes (styles scoped, non héritables ici),
   à l'image de la card sœur MealFeedbackStatsPanel. */
.stats-card {
  display: block;
  width: 100%;
  height: 100%;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.stats-block {
  margin-bottom: 1rem;
}

.stats-block:last-child {
  margin-bottom: 0;
}

.stats-block-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
  margin: 0 0 0.35rem 0;
}

.stats-state {
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
}

.stats-empty {
  font-style: italic;
}

.stats-global {
  margin: 0;
}

.stats-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.stats-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.stats-row:last-child {
  border-bottom: none;
}

.stats-label {
  font-weight: 600;
  color: #1a1a1a;
}

.stats-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stats-value--dish {
  font-size: 1.1rem;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}
</style>
